/**
 * Currency Shop panel — "Buy Gold" (real money, through core's deposit flow:
 * Proceed creates an order priced by the backend's `GoldCatalogue` and leaves
 * for the gateway's page — see `state/currency-shop-state.ts`). Modeled directly on
 * vendor-ui.ts's factory-function pattern (create / open / close / dispose,
 * `.panel.grained` + backdrop-click + Escape to close) and its square-tile
 * grid convention (aspect-ratio:1 well, caption below — Quartermaster's
 * Manifest reference). No confirm step here — that's reserved for the
 * Banker's one-way currency exchange, not a real-money purchase the platform
 * already gates behind its own payment flow.
 *
 * ─── The ladder, the slider and the method (2026-09-18) ─────────────────────
 * Janeks: *"Select prepared pack (8 packs, 5EUR to 956 EUR), below a slider to
 * select custom value, max = 1600 EUR. + select payment method (radio ->
 * labels) -> Card, BLIK, Apple Pay (last 2 in disabled state). Currently
 * 'proceed' button just credits Gold."*
 *
 * Three things are worth knowing about how this is put together:
 *
 *  - **The slider never quotes its own price.** It shows what the amount buys,
 *    computed by `goldForCents` — but Proceed sends CENTS (or the pack id),
 *    and the server's `GoldCatalogue` derives the Gold itself. The preview and the
 *    charge agree because they run the same rule, not because one told the
 *    other.
 *  - **The icon is the same rule as the price.** The brief gives the artwork
 *    rule as *"the image corresponds to sum chosen - >= pack price"*, which is
 *    exactly the rung the rate comes from. One function answers both, so the
 *    picture can never show a tier the player is not being charged at.
 *  - **The bounds come from the server.** They are `GoldCatalogue`'s rules; a
 *    slider that derived its own range from the pack list would be a second
 *    copy of them.
 */
import {
  currencyShopStateStore, loadCurrencyShop, closeCurrencyShop, proceed,
  selectPack, selectedPack, setCustomCents, setPaymentMethod,
} from '../../state/currency-shop-state';
import { openSettingsAtBilling } from '../../settings/settings-ui';
import type { CurrencyPackEntry, CurrencyShopState } from '../../state/currency-shop-state.types';
import { closeOtherModals, registerPanel } from './panels';
import { chipKeyTarget, paymentChipsHtml } from './payment-chips';
import {
  cashAmount, createWindowChrome, goldAmount, windowHeading,
} from './window-chrome';

let unsubscribe: (() => void) | null = null;
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;

/** Deregisters this panel from `ui/panels.ts` — see `registerPanel`. */
let unregisterPanel: (() => void) | null = null;

/**
 * The eight pack renders, cheapest first
 * (`assets/generated_items/build_gold_icons.py` -> `assets/ui/`). In
 * `assets/ui/` and not `assets/icons/`, for the reason `hud.ts` gives about the
 * coin: `paperdoll.ts` globs that directory as the ITEM map, and a currency
 * pack is not something you can pick up.
 *
 * Chosen by the pack's INDEX in the ladder, not by its key: `category`-style
 * per-key maps go stale the moment a rung is added, and this way a ladder of
 * any length still reads as ascending. Past the end it holds at the last icon
 * rather than wrapping back to dust.
 */
/**
 * The 256px renders, not the 64px ones.
 *
 * Janeks: *"Make the visualisations Larger, they are the main thing ... the
 * item icons can be larger, can even go over their container cards."* A 64px
 * source drawn at 110 is a blurred 64px source. `build_gold_icons.py` emits
 * three tiers and names the middle one BARE — `gold_coins.webp` is 256px,
 * `_slot` is 64 and `_full` is 512 (see its `tier == 'detail'` branch).
 *
 * The pattern EXCLUDES the other two tiers rather than matching everything and
 * sorting it out at lookup time. With `eager: true, query: '?url'` Vite emits
 * a build asset for every match, so a bare `gold_*.webp` would ship all 27
 * files — the unreferenced `_full` tier alone is ~300KB of dead weight in the
 * bundle. Negative patterns keep the emission and the lookup honest at once.
 */
const PACK_ART = import.meta.glob<string>([
  '../assets/ui/gold_*.webp',
  '!../assets/ui/gold_*_slot.webp',
  '!../assets/ui/gold_*_full.webp',
], { eager: true, query: '?url', import: 'default' });
const PACK_ICON_ORDER = [
  'gold_dust', 'gold_nugget', 'gold_coins', 'gold_ingot',
  'gold_ingot_pile', 'gold_jewelery', 'gold_duffelbag', 'gold_container',
];

export function packIconUrl(index: number): string {
  const name = PACK_ICON_ORDER[Math.min(Math.max(index, 0), PACK_ICON_ORDER.length - 1)];
  // The other tiers are excluded by the glob above, so this is an exact
  // filename match against a map that holds only the 256px renders.
  const hit = Object.entries(PACK_ART).find(([path]) => path.endsWith(`/${name}.webp`));
  return hit?.[1] ?? '';
}

export function createCurrencyShopUI(): HTMLElement {
  // One shell for every window — see `ui/window-chrome.ts`.
  const { root, header, body } = createWindowChrome({
    id: 'currency-shop-ui',
    headerId: 'currency-shop-header',
    bodyId: 'currency-shop-body',
    // Wider than the 760 it opened at — Janeks: *"Make pack selection cards in
    // 1 line, so that the whole screen fits without scrolling. Can make the
    // popup slightly larger."* Eight cards across need the room: 8 x ~118px
    // plus seven 10px gutters plus the shell's 2 x 18px padding is ~1050, and
    // `.bv-win` caps everything at `min(94vw, --win-w)` so a narrower screen
    // still gets a window that fits rather than one that overflows.
    width: 1060,
    label: 'Close currency shop',
    onClose: () => closeCurrencyShopUI(),
  });

  onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && root.style.display !== 'none') {
      e.preventDefault();
      closeCurrencyShopUI();
    }
  };
  document.addEventListener('keydown', onKeyDown);

  const rerender = () => {
    if (root.style.display === 'none') return;
    render(header, body, currencyShopStateStore.get());
  };
  unsubscribe = currencyShopStateStore.subscribe(rerender);

  // "Add billing address" sits in the header with the error it answers.
  header.addEventListener('click', (e) => {
    if (!(e.target as HTMLElement | null)?.closest('[data-open-billing]')) return;
    closeCurrencyShopUI();
    openSettingsAtBilling();
  });

  // Delegated, because the body is rewritten on every store push — including
  // on every drag of the slider.
  body.addEventListener('click', onBodyClick);
  body.addEventListener('input', onBodyInput);
  body.addEventListener('keydown', onBodyKeyDown);
  body.addEventListener('focusout', onBodyFocusOut);

  // One registry instead of the five hand-copied guard chains index.ts
  // used to carry. See ui/panels.ts.
  unregisterPanel?.();
  unregisterPanel = registerPanel({ id: 'currency-shop', isOpen: isCurrencyShopUIOpen, close: closeCurrencyShopUI });
  return root;
}

export function openCurrencyShopUI(): void {
  closeOtherModals('currency-shop');
  // See `closeCurrencyShopUI` — belt to that braces, so an open always paints.
  paintedKey = null;
  const el = document.getElementById('currency-shop-ui');
  if (el) el.style.display = 'flex';
  void loadCurrencyShop();
}

export function isCurrencyShopUIOpen(): boolean {
  const el = document.getElementById('currency-shop-ui');
  return !!el && el.style.display !== 'none';
}

export function closeCurrencyShopUI(): void {
  const el = document.getElementById('currency-shop-ui');
  if (el) el.style.display = 'none';
  // Forgetting the paint is not optional: `paintedKey` describes the DOM of a
  // particular body element, and the next open may be a DIFFERENT element
  // (`createCurrencyShopUI` builds a fresh shell on every settlement mount).
  // Left stale, a reopen whose state happens to produce the same key would
  // skip the build and show an empty panel.
  paintedKey = null;
  closeCurrencyShop();
}

export function disposeCurrencyShopUI(): void {
  paintedKey = null;
  unregisterPanel?.();
  unregisterPanel = null;
  unsubscribe?.();
  unsubscribe = null;
  if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
  onKeyDown = null;
}

// ─── the ladder rule ────────────────────────────────────────────────────────

/**
 * The rung a custom amount sits at — the pack whose rate prices it AND whose
 * art the slider shows. A deliberate duplicate of the server's `rungFor`
 * (`currency-shop.formulas.ts`), the same way `skipGoldCost` duplicates the
 * craft-skip price: the preview has to match what will be charged, and the
 * charge is re-derived server-side from the price either way.
 */
export function rungFor(cents: number, packs: CurrencyPackEntry[]): CurrencyPackEntry | null {
  let best: CurrencyPackEntry | null = null;
  for (const p of packs) {
    if (cents >= p.priceCents && (!best || p.priceCents > best.priceCents)) best = p;
  }
  return best;
}

const GOLD_PER_EUR_FALLBACK = 100;

/** Derives the gold-per-euro rate from the catalogue packs (all share the same flat rate). */
function goldPerEur(packs: CurrencyPackEntry[]): number {
  const p = packs.find((pk) => pk.priceCents > 0 && pk.premiumAmount > 0);
  if (!p) return GOLD_PER_EUR_FALLBACK;
  return Math.round(p.premiumAmount / (p.priceCents / 100));
}

/** What `cents` buys at the catalogue's flat rate. 0 below the cheapest pack. */
export function goldForCents(cents: number, packs: CurrencyPackEntry[]): number {
  if (!rungFor(cents, packs)) return 0;
  return Math.floor((cents * goldPerEur(packs)) / 100);
}

/**
 * The name of a Gold amount, shown on the slider from tablet width up. Janeks,
 * 2026-10-19 (each line is "from this much Gold"; his ranges overlapped at 1 499
 * and 50K, so each name starts where the next one's range ends):
 */
export const GOLD_TIERS: readonly { from: number; name: string }[] = [
  { from: 500, name: 'Gold Dust' },
  { from: 1_500, name: 'Nugget Pack' },
  { from: 3_000, name: 'Minted Coins' },
  { from: 8_000, name: 'Gold Bar' },
  { from: 15_000, name: 'Stack of Bars' },
  { from: 50_000, name: 'Rare Jewellery' },
  { from: 350_000, name: 'Duffel of Gold' },
  { from: 750_000, name: 'Shipment of Gold Bars' },
];

export function goldTierName(gold: number): string | null {
  let name: string | null = null;
  for (const t of GOLD_TIERS) if (gold >= t.from) name = t.name;
  return name;
}

// ─── the slider scale ───────────────────────────────────────────────────────

/**
 * Positions on the track. The slider runs EUR 5 to EUR 10 000 (Janeks,
 * 2026-10-19: *"The max transaction is 10K EUR. Adjust the slider."*), a
 * 2000:1 range. On a linear track every pixel would be about EUR 12 and the
 * whole EUR 5-100 band, where most players buy, would be a few pixels wide. So
 * the track is LOGARITHMIC: each tenth of it multiplies the amount by the same
 * factor, and the typed Amount field stays the exact control.
 */
export const SLIDER_STEPS = 1000;

/** Round amounts on the track: whole euros low down, coarser higher up. */
function snapStep(cents: number): number {
  if (cents < 10_000) return 100; // EUR 1 under EUR 100
  if (cents < 100_000) return 500; // EUR 5 under EUR 1 000
  if (cents < 500_000) return 2_500; // EUR 25 under EUR 5 000
  return 10_000; // EUR 100 above
}

/**
 * Track position -> cents. The ends are the bounds exactly; in between the
 * amount is snapped to a round step, and pulled onto a pack's price when it is
 * within 2% of it, so dragging onto a pack still lights its card.
 */
export function sliderPosToCents(pos: number, min: number, max: number, packs: readonly CurrencyPackEntry[]): number {
  if (pos <= 0 || max <= min) return min;
  if (pos >= SLIDER_STEPS) return max;
  const raw = min * (max / min) ** (pos / SLIDER_STEPS);
  for (const p of packs) {
    if (Math.abs(raw - p.priceCents) <= p.priceCents * 0.02) return p.priceCents;
  }
  const step = snapStep(raw);
  return Math.min(max, Math.max(min, Math.round(raw / step) * step));
}

/** Cents -> the nearest track position (the inverse of the curve, before snapping). */
export function centsToSliderPos(cents: number, min: number, max: number): number {
  if (max <= min || cents <= min) return 0;
  if (cents >= max) return SLIDER_STEPS;
  return Math.round((SLIDER_STEPS * Math.log(cents / min)) / Math.log(max / min));
}

// ─── rendering ──────────────────────────────────────────────────────────────

/**
 * What the last full paint drew.
 *
 * ─── This is the fix for the slider ─────────────────────────────────────────
 * Janeks: *"Black market slider - make it slide'able, not a single click. Drag
 * and slide option."*
 *
 * It WAS an `<input type="range">`, which drags natively. What stopped it was
 * this file: `render` did `body.innerHTML = ''`, the store pushed on every
 * `input` event, and so the element the browser was tracking the drag on was
 * destroyed on the first pixel of movement. The slider moved one step and
 * stuck — which reads exactly like a control that only accepts clicks.
 *
 * The same bug and the same fix as `settings-ui.ts`'s `renderMixer`, which
 * documents it: *"Replacing the markup mid-gesture detaches the element the
 * browser is tracking, so the slider would move once and then stick."*
 *
 * So `customCents` is deliberately NOT part of this key: moving the slider
 * repaints the readouts in place and never rebuilds. Everything that DOES
 * change the layout is in it — including the amount's pack identity, so
 * selecting a pack still rebuilds and lights the card up.
 */
let paintedKey: string | null = null;

/**
 * The structural key. Anything here rebuilds the body; anything else is
 * patched in place by `patchCheckout`.
 *
 * The SELECTION is deliberately not here either, and that was a real bug for
 * one iteration: keying on `selectedPack(state)?.key` looks harmless — it
 * changes only when the amount crosses a pack price rather than on every step
 * — but crossing a pack price is something a drag does constantly, and each
 * crossing rebuilt the body and killed the gesture. Dragging off EUR 5.00, the
 * very first thing a player does, was enough. Caught by the identity test in
 * `currency-shop-ui.test.ts`.
 *
 * So the highlight is patched in place by `patchCheckout` instead, along with
 * the Proceed label and the art. Nothing that a slider move can touch is in
 * this key.
 */
function structuralKey(state: CurrencyShopState): string {
  return JSON.stringify({
    packs: state.packs.map((p) => p.key),
    min: state.customMinCents,
    max: state.customMaxCents,
    method: state.paymentMethodId,
    // Which methods are live decides the note under the checkout.
    methods: state.methods.map((m) => `${m.id}:${m.enabled}`),
    busy: state.busy,
    empty: state.packs.length === 0,
  });
}

function render(header: HTMLElement, body: HTMLElement, state: CurrencyShopState): void {
  renderHeader(header, state);

  const key = structuralKey(state);
  if (key !== paintedKey) {
    paintedKey = key;
    buildBody(body, state);
  }
  // Always: the live numbers, the slider's own value, the floating art. None
  // of these owns focus or a gesture, and all of them move with the slider.
  patchCheckout(body, state);
}

function renderHeader(header: HTMLElement, state: CurrencyShopState): void {
  // Cash and Gold, in the two treatments used everywhere else: Cash in bone
  // with its unit spelled out, Gold in brass behind the real coin art.
  const balance = state.balance
    ? `<p>${cashAmount(state.balance.cash)} · ${goldAmount(state.balance.gold)}</p>`
    : '';
  // No subtitle. It read "Premium currency — real money." and Janeks cut it:
  // the panel is called Buy Gold, the prices are in euros, and a line
  // explaining that money is money was the only copy on the screen that told
  // the player nothing.
  header.innerHTML = `
    ${windowHeading('Buy Gold')}
    ${balance}
    ${state.lastReceipt ? `<p class="bv-win-ok">${esc(state.lastReceipt)}</p>` : ''}
    ${state.error ? `<p class="bv-win-err">${esc(state.error)}</p>` : ''}
    ${state.needsBilling ? '<p><button type="button" class="bv-act bv-act-go" data-open-billing>Add billing address</button></p>' : ''}
  `;
}

function buildBody(body: HTMLElement, state: CurrencyShopState): void {
  body.innerHTML = '';

  // `state.packs` can be an empty array before the fetch lands. It used to be
  // able to be UNDEFINED too, when the response omitted the field — that threw
  // from inside a store push and surfaced as an unhandled rejection attributed
  // to an unrelated test. Fixed where it belongs, in `loadCurrencyShop`.
  if (state.packs.length === 0) {
    const note = document.createElement('p');
    note.className = 'bv-win-empty';
    note.textContent = state.busy ? 'Loading…' : 'No Gold packs available.';
    body.appendChild(note);
    return;
  }

  const grid = document.createElement('div');
  grid.className = 'bv-cshop-grid';
  const chosen = selectedPack(state);
  state.packs.forEach((pack, i) => {
    grid.appendChild(packTile(pack, i, pack.key === chosen?.key));
  });
  body.appendChild(grid);

  const checkout = checkoutSection(state);
  if (checkout) body.appendChild(checkout);
}

/**
 * A pack card. No Buy button — Janeks: *"Buy Button can be ditched from packs.
 * Select pack - it is highlighted and the slider amount is bumped to it."*
 *
 * The whole card is the button, which is also why it is a real `<button>`: a
 * clickable `<div>` would need a role, a tabindex and a keydown handler to get
 * back what this has for free, and eight of them are the primary controls on
 * the screen.
 */
function packTile(pack: CurrencyPackEntry, index: number, on: boolean): HTMLElement {
  const tile = document.createElement('button');
  tile.type = 'button';
  tile.className = `bv-cshop-card${on ? ' bv-cshop-card-on' : ''}`;
  tile.setAttribute('aria-pressed', String(on));
  tile.dataset.selectPack = pack.key;
  const art = packIconUrl(index);
  // The art is in its own overflow-visible box so it can stand proud of the
  // card — see `.bv-cshop-art` in styles/commerce.css.ts.
  tile.innerHTML = `
    <span class="bv-cshop-art-box">
      ${art ? `<img class="bv-cshop-art" src="${art}" alt="" aria-hidden="true" />` : ''}
    </span>
    <span class="bv-cshop-amount">${goldAmount(pack.premiumAmount)}</span>
    <span class="bv-cshop-price">${esc(formatPrice(pack.priceCents, pack.currency))}</span>
  `;
  return tile;
}

/**
 * The checkout: the amount, the slider, the provider chips and Proceed.
 *
 * Always present — Janeks: *"slider is like a checkout step, only always
 * there"*. Selecting a pack bumps the slider to its price rather than
 * bypassing this section, so there is exactly one place the player confirms
 * what they are about to spend.
 *
 * Returns null until the bounds arrive: a slider drawn before them would have
 * to invent a range, which is the one thing this section must not do.
 */
function checkoutSection(state: CurrencyShopState): HTMLElement | null {
  const { customMinCents: min, customMaxCents: max, customCents: cents } = state;
  if (min === null || max === null || cents === null) return null;

  const wrap = document.createElement('div');
  wrap.className = 'bv-cshop-checkout';
  /*
   * ─── The layout ───────────────────────────────────────────────────────────
   * Janeks: *"there is a lot of empty space. Try to think harder on a good
   * layout approach ... Use thw whole horizontal sapce"*.
   *
   * The first version was one centred column in a 1000px box, so everything
   * below the slider sat in the left half and the right half was blank. Three
   * bands now, each using the full width for something different:
   *
   *   STAGE    the hero art and the Gold it buys — what you GET, centred and
   *            the biggest thing on the panel.
   *   TRACK    the slider, edge to edge. It is the primary control and the one
   *            element that genuinely wants every pixel of width.
   *   ACTIONS  one row: the typed amount and the payment chips packed left,
   *            Proceed pushed hard right by an `auto` margin. Reading
   *            left-to-right that is choose → how → commit, and it is what
   *            fills the width that used to be empty.
   *
   * The note moves under the actions rather than above Proceed, where it was
   * separating a button from the control it belongs to.
   */
  wrap.innerHTML = `
    <div class="bv-cshop-stage">
      <div class="bv-cshop-float" data-float></div>
      <!-- Janeks: "The amount of gold is above the slider, next to the Gold
           icon." goldAmount() is the shared coin+number treatment, so this is
           the same Gold the HUD and the pack cards show. -->
      <p class="bv-cshop-total" data-total></p>
    </div>
    <div class="bv-cshop-slider-row">
      <div class="bv-cshop-track">
        <!-- The bounds sit ABOVE the ends of the track, not beside it: the
             thumb label below is centred on the thumb, so at either extreme it
             lands exactly where a beside-the-track bound would be and the two
             overlapped. -->
        <span class="bv-cshop-bound bv-cshop-bound-lo" data-bound-min></span>
        <span class="bv-cshop-bound bv-cshop-bound-hi" data-bound-max></span>
        <!-- A position on a logarithmic track, not cents: see SLIDER_STEPS. -->
        <input type="range" class="bv-cshop-slider" data-custom-slider
          min="0" max="${SLIDER_STEPS}" step="1" value="${centsToSliderPos(cents, min, max)}"
          aria-label="Amount to spend" />
        <!-- Follows the thumb. Positioned from JS because no CSS expression
             can read a range input's thumb offset. -->
        <span class="bv-cshop-thumb-label" data-thumb-label></span>
      </div>
    </div>
    <div class="bv-cshop-actions">
      <!-- Janeks: "Have inputs styled properly for EUR value." The slider is
           coarse (100-cent steps) and imprecise to land on a figure; this is
           how you say EXACTLY what you want to spend. Kept in sync with the
           slider both ways -- see commitAmountField for why it clamps on
           commit and not on every keystroke. -->
      <label class="bv-cshop-amount-field">
        <span class="bv-cshop-amount-label">Amount</span>
        <span class="bv-cshop-amount-box">
          <span class="bv-cshop-amount-cur" aria-hidden="true">${esc(currencySymbol(state))}</span>
          <input type="text" inputmode="decimal" class="bv-cshop-amount-input"
            data-amount-input autocomplete="off" spellcheck="false"
            aria-label="Amount to spend" />
        </span>
      </label>
      <div class="bv-cshop-pay" data-chips></div>
      <div class="bv-cshop-commit" data-commit></div>
    </div>
    <p class="bv-win-note">${esc(checkoutNote(state))}</p>
  `;

  const proceedBtn = document.createElement('button');
  proceedBtn.type = 'button';
  proceedBtn.className = 'bv-cshop-proceed';
  proceedBtn.dataset.proceed = 'true';
  // Into the actions row rather than appended to the section, so it is the
  // right-hand end of that row instead of a block under it.
  wrap.querySelector('[data-commit]')?.appendChild(proceedBtn);

  return wrap;
}

/**
 * Repaints everything that moves with the slider, WITHOUT touching the slider
 * itself while it is being dragged.
 *
 * The `activeElement` check is the second half of the drag fix and is not
 * redundant with `structuralKey`: writing `slider.value` during a drag fights
 * the browser's own thumb tracking, so the value is only pushed IN when the
 * change came from somewhere else — selecting a pack, or the panel opening.
 * Same reasoning as `renderMixer`'s `dragging` branch.
 */
function patchCheckout(body: HTMLElement, state: CurrencyShopState): void {
  const { customMinCents: min, customMaxCents: max, customCents: cents } = state;
  if (min === null || max === null || cents === null) return;
  const slider = body.querySelector<HTMLInputElement>('[data-custom-slider]');
  if (!slider) return;

  const currency = state.packs[0]?.currency ?? 'EUR';
  const gold = goldForCents(cents, state.packs);
  const rung = rungFor(cents, state.packs);
  const rungIndex = rung ? state.packs.findIndex((p) => p.key === rung.key) : 0;
  const chosen = selectedPack(state);

  const pos = centsToSliderPos(cents, min, max);
  if (document.activeElement !== slider && slider.value !== String(pos)) {
    slider.value = String(pos);
  }
  // The track is a position, so the amount it stands for is spelled out.
  slider.setAttribute('aria-valuetext', `${formatPrice(cents, currency)}, ${gold} Gold`);

  // The pack highlight, per card. Patched rather than rebuilt — see
  // `structuralKey` for what happens when this is keyed instead.
  for (const card of body.querySelectorAll<HTMLElement>('[data-select-pack]')) {
    const on = card.dataset.selectPack === chosen?.key;
    card.classList.toggle('bv-cshop-card-on', on);
    card.setAttribute('aria-pressed', String(on));
  }

  setText(body, '[data-bound-min]', formatPrice(min, currency));
  setText(body, '[data-bound-max]', formatPrice(max, currency));

  const total = body.querySelector<HTMLElement>('[data-total]');
  if (total) total.innerHTML = goldAmount(gold);

  // The EUR label under the thumb. `(value - min) / (max - min)` is the
  // fraction; the 0.5em..(100% - 0.5em) inset keeps the label centred on the
  // thumb rather than running off the ends of the track, which a plain
  // percentage does at both extremes.
  // Same discipline as the slider: the store is the source of truth, but a
  // field the player is typing in must not have its value rewritten underneath
  // them mid-edit — that would move the caret and fight every keystroke.
  const amountField = body.querySelector<HTMLInputElement>('[data-amount-input]');
  if (amountField && document.activeElement !== amountField) {
    const wanted = amountFieldText(cents);
    if (amountField.value !== wanted) amountField.value = wanted;
  }

  const label = body.querySelector<HTMLElement>('[data-thumb-label]');
  if (label) {
    const frac = pos / SLIDER_STEPS;
    // The amount's name, then its price. The name is hidden on phones (CSS);
    // with it the label is wide, so it leans away from the nearer end instead
    // of centring on the thumb and running off the track.
    const name = goldTierName(gold);
    label.innerHTML = `${name ? `<span class="bv-cshop-tier-name">${esc(name)}</span>` : ''}${esc(formatPrice(cents, currency))}`;
    label.style.left = `calc(${(frac * 100).toFixed(3)}% + ${(0.5 - frac).toFixed(3)} * var(--cshop-thumb))`;
    label.style.transform = `translateX(-${(frac * 100).toFixed(1)}%)`;
  }

  const floatHost = body.querySelector<HTMLElement>('[data-float]');
  if (floatHost) {
    // Only when the picture actually changes — otherwise every step of the
    // drag would restart the entrance animation.
    const was = floatHost.dataset.rung;
    if (was !== String(rungIndex)) {
      // No previous rung (first paint) counts as rising, so the art enters
      // rather than appearing.
      const up = was === undefined || rungIndex > Number(was);
      floatHost.dataset.rung = String(rungIndex);
      floatHost.innerHTML = floatArtHtml(rungIndex, up);
    }
  }

  const chips = body.querySelector<HTMLElement>('[data-chips]');
  if (chips) {
    chips.innerHTML = paymentChipsHtml(state.methods, {
      selectedId: state.paymentMethodId,
      busy: state.busy,
    });
  }

  const btn = body.querySelector<HTMLButtonElement>('[data-proceed]');
  if (btn) {
    btn.disabled = state.busy || gold <= 0 || !state.paymentMethodId;
    // Names what is being bought, so the button is the confirmation rather
    // than a bare verb next to a number.
    btn.textContent = state.busy
      ? 'Working…'
      : `Proceed — ${formatPrice(cents, currency)}${chosen ? '' : ' (custom)'}`;
  }
}

/**
 * The one line under the checkout. Payment is core's: Proceed leaves for the
 * gateway's page and comes back here; no method live means an admin has not
 * switched one on in MAD (or the terminal is not configured).
 */
function checkoutNote(state: CurrencyShopState): string {
  if (state.busy && state.methods.length === 0) return 'Loading payment methods…';
  if (!state.methods.some((m) => m.enabled)) return 'Payments are not available right now.';
  return 'Proceed opens the secure payment page; you come back here when it is done.';
}

/** The symbol the ladder is priced in, for the field's prefix. */
function currencySymbol(state: CurrencyShopState): string {
  const code = state.packs[0]?.currency ?? 'EUR';
  return code === 'EUR' ? '€' : code === 'USD' ? '$' : code;
}

/**
 * Reads the typed amount and, if it is a usable number, pushes it.
 *
 * ─── Why it does NOT clamp here ─────────────────────────────────────────────
 * Clamping on every keystroke makes the field impossible to type in. The floor
 * is EUR 5, so the moment you clear the box and press `1` on your way to
 * typing `15`, a clamping handler rewrites it to `5` — and the caret with it.
 * The same goes for the empty string, and for `2.` mid-way through `2.50`.
 *
 * So: out-of-range and half-typed values are simply not pushed. The field holds
 * whatever was typed, the store keeps the last good value, and
 * `commitAmountField` does the clamping once the player is finished.
 */
function onAmountInput(input: HTMLInputElement): void {
  const cents = parseAmount(input.value);
  if (cents === null) return;
  const { customMinCents: min, customMaxCents: max } = currencyShopStateStore.get();
  if (min === null || max === null) return;
  if (cents < min || cents > max) return;
  setCustomCents(cents);
}

/**
 * Finishes the edit: clamps, pushes, and rewrites the box in canonical form.
 *
 * Called on blur and on Enter. An unparseable box falls back to the amount the
 * store already holds rather than to the floor — a player who selected the
 * EUR 956 pack and then typed rubbish should not silently end up at EUR 5.
 */
function commitAmountField(input: HTMLInputElement): void {
  const state = currencyShopStateStore.get();
  const { customMinCents: min, customMaxCents: max, customCents: current } = state;
  if (min === null || max === null) return;
  const parsed = parseAmount(input.value);
  const cents = parsed === null
    ? (current ?? min)
    : Math.min(max, Math.max(min, parsed));
  setCustomCents(cents);
  input.value = amountFieldText(cents);
}

/**
 * Cents from what was typed, or null if it is not a number yet.
 *
 * Accepts a comma as the decimal mark: this panel is priced in euros and shown
 * to players whose keyboards and habits put a comma there. Everything else —
 * spaces, a stray currency symbol someone pasted — is dropped rather than
 * rejected, because rejecting it means the field just stops responding with no
 * explanation.
 */
export function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[^0-9.,]/g, '').replace(',', '.');
  if (cleaned === '' || cleaned === '.') return null;
  const euros = Number(cleaned);
  if (!Number.isFinite(euros) || euros < 0) return null;
  return Math.round(euros * 100);
}

/** Cents as the field shows them: plain digits, two decimals, no symbol. */
export function amountFieldText(cents: number): string {
  return (cents / 100).toFixed(2);
}

function setText(root: HTMLElement, sel: string, text: string): void {
  const el = root.querySelector<HTMLElement>(sel);
  if (el) el.textContent = text;
}

/**
 * The art over the slider, keyed by rung.
 *
 * Janeks: *"On change we have a fly in + fade animation (vertical movement to
 * substitute the icon + fade, old one goes up and fades, while new one comes up
 * from bottom side and fades in)."* And now also: *"The changing icon can be
 * larger and flying over card there."*
 *
 * The entrance class is baked into the markup rather than added afterwards,
 * because `patchCheckout` only writes this HTML when the rung actually
 * changed — so the animation plays exactly once per change and never restarts
 * mid-drag. That replaces `playRungSwap`, which existed to re-find and
 * re-class an element the old full-body re-render had just replaced.
 *
 * `up` is which way the ladder moved: a bigger pack rises into place from
 * below, a smaller one drops in from above.
 */
function floatArtHtml(rungIndex: number, up: boolean): string {
  const url = packIconUrl(rungIndex);
  if (!url) return '';
  const cls = `bv-cshop-float-art ${up ? 'bv-cshop-float-in' : 'bv-cshop-float-in-down'}`;
  return `<img class="${cls}" src="${url}" alt="" aria-hidden="true" />`;
}

// ─── events ─────────────────────────────────────────────────────────────────

function onBodyClick(e: Event): void {
  const target = e.target as HTMLElement | null;
  // A pack card SELECTS now — it does not buy. The only thing that spends
  // money on this panel is Proceed.
  // `dataset.selectPack` lands in the DOM as `data-select-pack` — the
  // attribute is kebab-case, the property is camel.
  const pack = target?.closest<HTMLElement>('[data-select-pack]');
  if (pack?.dataset.selectPack) { selectPack(pack.dataset.selectPack); return; }
  if (target?.closest('[data-proceed]')) { void proceed(); return; }
  // The chips are buttons now, not radios, so the id is on the dataset rather
  // than on `value`.
  const chip = target?.closest<HTMLElement>('[data-pay-method]');
  if (chip?.dataset.payMethod) setPaymentMethod(chip.dataset.payMethod);
}

/**
 * Arrow keys inside the provider chip row.
 *
 * A native radio group does this for free and the chips are buttons (a radio
 * cannot carry a logo), so it is wired by hand — see `payment-chips.ts`. Only
 * fires while focus is actually on a chip, so the slider's own arrow-key
 * stepping is untouched.
 */
function onBodyKeyDown(e: KeyboardEvent): void {
  const field = (e.target as HTMLElement | null)?.closest<HTMLInputElement>('[data-amount-input]');
  if (field) {
    if (e.key !== 'Enter') return;
    // Not a submit: this panel's Enter finishes the edit, it does not spend
    // money. Proceed stays a deliberate second action.
    e.preventDefault();
    commitAmountField(field);
    return;
  }
  const chip = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-pay-method]');
  if (!chip) return;
  const { methods, paymentMethodId } = currencyShopStateStore.get();
  const next = chipKeyTarget(e.key, methods, paymentMethodId);
  if (!next) return;
  e.preventDefault();
  setPaymentMethod(next);
  // Focus follows the selection, which is what makes the group keyboard-usable
  // — the newly selected chip is the only one with tabindex 0.
  const body = document.getElementById('currency-shop-body');
  body?.querySelector<HTMLElement>(`[data-pay-method="${next}"]`)?.focus();
}

/**
 * Every step of the drag: write the amount and nothing else.
 *
 * The store push that follows reaches `render`, whose structural key omits
 * `customCents` — so the body is NOT rebuilt and this input element survives
 * the gesture. That is the whole slider fix; see `paintedKey`.
 */
function onBodyInput(e: Event): void {
  const target = e.target as HTMLElement | null;
  const slider = target?.closest<HTMLInputElement>('[data-custom-slider]');
  if (slider) {
    const { customMinCents: min, customMaxCents: max, packs } = currencyShopStateStore.get();
    if (min !== null && max !== null) setCustomCents(sliderPosToCents(Number(slider.value), min, max, packs));
    return;
  }
  const field = target?.closest<HTMLInputElement>('[data-amount-input]');
  if (field) onAmountInput(field);
}

/** Blur finishes an edit of the typed amount — see `commitAmountField`. */
function onBodyFocusOut(e: Event): void {
  const field = (e.target as HTMLElement | null)?.closest<HTMLInputElement>('[data-amount-input]');
  if (field) commitAmountField(field);
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}

export function formatPrice(cents: number, currency: string): string {
  const amount = (cents / 100).toFixed(2);
  const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : `${currency} `;
  return `${symbol}${amount}`;
}
